/**
 * Bausteine des Wizards „Neue Kampagne“ — Maße und Farben exakt aus dem Design
 * (`Neue Kampagne.dc.html`), damit alle Schritte gleich aussehen.
 */

import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import React, { useEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import { wz } from './wizard-tokens';

const useStyles = makeStyles()({
  card: {
    background: '#FFF',
    borderRadius: 12,
    boxShadow: wz.shadowCard,
    padding: 28,
    display: 'flex',
    flexDirection: 'column',
  },
  heading: { display: 'flex', flexDirection: 'column', gap: 4 },
  headingTitle: { fontSize: 20, fontWeight: 700, color: wz.text },
  headingSub: { fontSize: 15, color: wz.muted },
  label: { fontSize: 15, fontWeight: 700, color: wz.text },
  section: { borderTop: `1px solid ${wz.border}`, display: 'flex', flexDirection: 'column' },
  chip: {
    fontSize: 13,
    borderRadius: 14,
    padding: '4px 10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    '&.removable': { padding: '4px 6px 4px 10px', gap: 4 },
    '&.blue': { background: wz.primarySoft, color: wz.primary },
    '&.grey': { background: wz.page, color: wz.textDim },
    '&.red': { background: wz.redBg, color: wz.redFg },
    '&.warn': { background: wz.warnBg, color: wz.warnFg },
    '&.clickable': { cursor: 'pointer' },
  },
  chipClose: { fontSize: 14, opacity: 0.7 },
  chipCloseButton: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: 'inherit',
  },
  chipSuffix: {
    border: 'none',
    fontFamily: 'inherit',
    background: '#FFF',
    borderRadius: 10,
    padding: '0 6px',
    cursor: 'pointer',
    fontSize: 12,
    color: 'inherit',
  },
  addChip: {
    fontSize: 13,
    borderRadius: 14,
    padding: '4px 10px',
    border: `1px dashed ${wz.dashed}`,
    color: wz.muted,
    cursor: 'pointer',
    background: 'transparent',
    fontFamily: 'inherit',
    '&:hover': { borderColor: wz.primary, color: wz.primary },
  },
  inlineInput: {
    fontSize: 13,
    borderRadius: 14,
    padding: '4px 10px',
    border: `1px solid ${wz.primary}`,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#FFF',
    color: wz.text,
  },
  choice: {
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    border: `1px solid ${wz.border}`,
    background: '#FFF',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: wz.text,
    '&.selected': { border: `1.5px solid ${wz.primary}`, background: wz.selectedBg },
  },
  option: {
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex',
    gap: 10,
    cursor: 'pointer',
    border: `1px solid ${wz.border}`,
    background: '#FFF',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: wz.text,
    '&.selected': { border: `1.5px solid ${wz.primary}`, background: wz.selectedBg },
  },
  selectPill: {
    fontSize: 14,
    padding: '6px 12px',
    borderRadius: 16,
    cursor: 'pointer',
    border: `1px solid ${wz.border}`,
    background: '#FFF',
    color: wz.text,
    fontFamily: 'inherit',
    '&.selected': {
      border: `1px solid ${wz.primary}`,
      background: wz.primarySoft,
      color: wz.primary,
    },
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  switchTrack: {
    width: 36,
    height: 20,
    borderRadius: 10,
    position: 'relative',
    flexShrink: 0,
    transition: 'background 150ms',
    background: wz.dashed,
    '&.on': { background: wz.primary },
  },
  switchKnob: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#FFF',
    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
    transition: 'left 150ms',
    '&.on': { left: 18 },
  },
  input: {
    border: 'none',
    outline: 'none',
    background: wz.page,
    borderRadius: 10,
    padding: '8px 12px',
    height: 39,
    boxSizing: 'border-box',
    fontSize: 15,
    color: wz.text,
    fontFamily: 'inherit',
    width: '100%',
    '&.small': { height: 36 },
    '&.invalid': { boxShadow: `0 0 0 1px ${wz.redFg}` },
  },
  textarea: {
    border: 'none',
    outline: 'none',
    resize: 'none',
    background: wz.page,
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    lineHeight: 1.5,
    color: wz.text,
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  infoBar: {
    fontSize: 13,
    color: wz.text555,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: wz.page,
    borderRadius: 8,
    padding: '8px 12px',
  },
  gradientButton: {
    height: 40,
    padding: '0 24px',
    boxSizing: 'border-box',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    color: '#FFF',
    background: wz.gradient,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
    '&:hover': { background: wz.gradientHover },
    '&.disabled': { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' },
  },
  outlineButton: {
    fontSize: 14,
    color: wz.primary,
    border: '1px solid rgba(79,68,255,0.5)',
    borderRadius: 8,
    padding: '6px 12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    background: '#FFF',
    fontFamily: 'inherit',
    '&.disabled': { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' },
  },
  link: {
    fontSize: 14,
    color: wz.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
  spin: {
    animation: 'wizSpin 1.4s linear infinite',
    '@keyframes wizSpin': { to: { transform: 'rotate(360deg)' } },
  },
});

/* ------------------------------------------------------------------ */

export const WizCard: React.FC<{ gap?: number; children: React.ReactNode; className?: string }> = ({
  gap = 22,
  children,
  className,
}) => {
  const { classes, cx } = useStyles();
  return (
    <div className={cx(classes.card, className)} style={{ gap }}>
      {children}
    </div>
  );
};

export const WizHeading: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode }> = ({
  title,
  subtitle,
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.heading}>
      <div className={classes.headingTitle}>{title}</div>
      {subtitle ? <div className={classes.headingSub}>{subtitle}</div> : null}
    </div>
  );
};

export const WizLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { classes } = useStyles();
  return <div className={classes.label}>{children}</div>;
};

/** Abschnitt mit Trennlinie oben (`border-top` + `padding-top`). */
export const WizSection: React.FC<{
  paddingTop?: number;
  gap?: number;
  children: React.ReactNode;
}> = ({ paddingTop = 20, gap = 14, children }) => {
  const { classes } = useStyles();
  return (
    <div className={classes.section} style={{ paddingTop, gap }}>
      {children}
    </div>
  );
};

export const MiSpinner: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color = wz.primary,
}) => {
  const { classes } = useStyles();
  return <AutorenewIcon className={classes.spin} sx={{ fontSize: size, color }} />;
};

interface ChipProps {
  variant?: 'blue' | 'grey' | 'red' | 'warn';
  icon?: React.ReactNode;
  /** Kleines weißes Feld hinter dem Text (z. B. Radius „+ 50 km“). */
  suffix?: React.ReactNode;
  onSuffixClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onRemove?: () => void;
  /** Klick auf den Chip (z. B. „bitte bestätigen“ öffnet die Ortssuche). */
  onClick?: () => void;
  children: React.ReactNode;
  title?: string;
}

export const WizChip: React.FC<ChipProps> = ({
  variant = 'blue',
  icon,
  suffix,
  onSuffixClick,
  onRemove,
  onClick,
  children,
  title,
}) => {
  const { classes, cx } = useStyles();
  return (
    <div
      className={cx(
        classes.chip,
        variant,
        onRemove ? 'removable' : undefined,
        onClick ? 'clickable' : undefined
      )}
      title={title}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      {icon}
      {children}
      {suffix !== undefined ? (
        onSuffixClick ? (
          <button
            type="button"
            className={classes.chipSuffix}
            onClick={e => {
              e.stopPropagation();
              onSuffixClick(e);
            }}
          >
            {suffix}
          </button>
        ) : (
          <span className={classes.chipSuffix}>{suffix}</span>
        )
      ) : null}
      {onRemove ? (
        <button
          type="button"
          className={classes.chipCloseButton}
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="×"
        >
          <CloseIcon className={classes.chipClose} />
        </button>
      ) : null}
    </div>
  );
};

export const WizAddChip: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => {
  const { classes } = useStyles();
  return (
    <button type="button" className={classes.addChip} onClick={onClick}>
      {label}
    </button>
  );
};

interface InlineInputProps {
  value: string;
  placeholder: string;
  width: number;
  onChange: (value: string) => void;
  /** Enter: Wert übernehmen, Eingabe bleibt offen (oder schließt, je nach Aufrufer). */
  onEnter: (value: string) => void;
  onEscape: () => void;
  onBlur: (value: string) => void;
}

/** Eingabe in Chip-Form („Stadt oder Region + Enter“): Enter übernimmt, Escape bricht ab, Blur übernimmt. */
export const WizInlineInput: React.FC<InlineInputProps> = ({
  value,
  placeholder,
  width,
  onChange,
  onEnter,
  onEscape,
  onBlur,
}) => {
  const { classes } = useStyles();
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <input
      ref={ref}
      className={classes.inlineInput}
      style={{ width }}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Escape') onEscape();
        else if (e.key === 'Enter') onEnter((e.target as HTMLInputElement).value);
      }}
      onBlur={e => onBlur(e.target.value)}
    />
  );
};

interface ChoiceProps {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  padding?: number | string;
  gap?: number;
  ariaLabel?: string;
  /** Nicht wählbar (z. B. Klasse C: wird nie angesprochen). */
  disabled?: boolean;
}

/** Große Auswahlkarte (Methode, Klassen). */
export const WizChoiceCard: React.FC<ChoiceProps> = ({
  selected,
  onClick,
  children,
  padding = 16,
  gap = 8,
  ariaLabel,
  disabled,
}) => {
  const { classes, cx } = useStyles();
  return (
    <button
      type="button"
      className={cx(classes.choice, selected ? 'selected' : undefined)}
      style={{
        padding,
        gap,
        flexDirection: 'column',
        ...(disabled ? { opacity: 0.6, cursor: 'default', background: wz.page } : {}),
      }}
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
};

export const WizRadioIcon: React.FC<{ checked: boolean; size?: number }> = ({
  checked,
  size = 20,
}) =>
  checked ? (
    <RadioButtonCheckedIcon sx={{ fontSize: size, color: wz.primary }} />
  ) : (
    <RadioButtonUncheckedIcon sx={{ fontSize: size, color: wz.dashed }} />
  );

export const WizCheckIcon: React.FC<{ checked: boolean; size?: number }> = ({
  checked,
  size = 22,
}) =>
  checked ? (
    <CheckBoxIcon sx={{ fontSize: size, color: wz.primary }} />
  ) : (
    <CheckBoxOutlineBlankIcon sx={{ fontSize: size, color: wz.dashed }} />
  );

interface OptionProps {
  selected: boolean;
  onClick: () => void;
  label: React.ReactNode;
  description?: React.ReactNode;
  /** Checkbox statt Radio. */
  checkbox?: boolean;
}

/** Antwortkarte mit Radio und Beschriftung (Schritt 5). */
export const WizOption: React.FC<OptionProps> = ({
  selected,
  onClick,
  label,
  description,
  checkbox,
}) => {
  const { classes, cx } = useStyles();
  return (
    <button
      type="button"
      className={cx(classes.option, selected ? 'selected' : undefined)}
      onClick={onClick}
      aria-pressed={selected}
    >
      {checkbox ? (
        <WizCheckIcon checked={selected} size={20} />
      ) : (
        <WizRadioIcon checked={selected} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {description ? (
          <div style={{ fontSize: 13, color: wz.muted, lineHeight: 1.4 }}>{description}</div>
        ) : null}
      </div>
    </button>
  );
};

/** Auswahl-Pille (Sprache, Optionen). */
export const WizSelectPill: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ selected, onClick, children }) => {
  const { classes, cx } = useStyles();
  return (
    <button
      type="button"
      className={cx(classes.selectPill, selected ? 'selected' : undefined)}
      onClick={onClick}
      aria-pressed={selected}
    >
      {children}
    </button>
  );
};

interface SwitchProps {
  on: boolean;
  onToggle: () => void;
  label: React.ReactNode;
  description?: React.ReactNode;
}

export const WizSwitch: React.FC<SwitchProps> = ({ on, onToggle, label, description }) => {
  const { classes, cx } = useStyles();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className={classes.switchRow}
      onClick={onToggle}
    >
      <div className={cx(classes.switchTrack, on ? 'on' : undefined)}>
        <div className={cx(classes.switchKnob, on ? 'on' : undefined)} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: wz.text }}>{label}</div>
        {description ? <div style={{ fontSize: 13, color: wz.muted }}>{description}</div> : null}
      </div>
    </button>
  );
};

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'size'
> {
  value: string;
  onChange: (value: string) => void;
  small?: boolean;
  invalid?: boolean;
}

export const WizInput: React.FC<InputProps> = ({
  value,
  onChange,
  small,
  invalid,
  className,
  ...rest
}) => {
  const { classes, cx } = useStyles();
  return (
    <input
      {...rest}
      className={cx(
        classes.input,
        small ? 'small' : undefined,
        invalid ? 'invalid' : undefined,
        className
      )}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  );
};

interface TextAreaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange'
> {
  value: string;
  onChange: (value: string) => void;
}

export const WizTextArea: React.FC<TextAreaProps> = ({ value, onChange, className, ...rest }) => {
  const { classes, cx } = useStyles();
  return (
    <textarea
      {...rest}
      className={cx(classes.textarea, className)}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  );
};

export const WizInfoBar: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  children,
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.infoBar}>
      {icon}
      {children}
    </div>
  );
};

interface GradientButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit';
}

export const WizGradientButton: React.FC<GradientButtonProps> = ({
  onClick,
  children,
  icon,
  disabled,
  loading,
  type = 'button',
}) => {
  const { classes, cx } = useStyles();
  return (
    <button
      type={type}
      className={cx(classes.gradientButton, disabled || loading ? 'disabled' : undefined)}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {children}
      {loading ? <MiSpinner size={18} color="#FFF" /> : icon}
    </button>
  );
};

export const WizOutlineButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
}> = ({ onClick, children, icon, disabled }) => {
  const { classes, cx } = useStyles();
  return (
    <button
      type="button"
      className={cx(classes.outlineButton, disabled ? 'disabled' : undefined)}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {children}
    </button>
  );
};

export const WizLink: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  color?: string;
  disabled?: boolean;
}> = ({ onClick, children, color, disabled }) => {
  const { classes } = useStyles();
  return (
    <button
      type="button"
      className={classes.link}
      style={{ color, opacity: disabled ? 0.5 : 1 }}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};
